import { displayMoneyValue, limitDecimals, maskMoneyTyping } from "../lib/money";
import { useEffect, useRef } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

/** `required` marca o campo com "*" (convenção das caixas de lançamento); campos sem "*" são opcionais e não levam rótulo "(opcional)". */
export function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}{required && <span className="req" aria-hidden="true"> *</span>}</span>
      {children}
    </label>
  );
}

/**
 * `decimals={2}` = campo de dinheiro com máscara "caixa eletrônico" (digitar 1 -> 0,01; depois 0 -> 0,10).
 * `negative` permite o sinal "-" nesse campo. Outros valores de `decimals` apenas limitam as casas; sem `decimals` o campo é livre.
 */
export const Input = ({ decimals, negative, onChange, value, ...p }: InputHTMLAttributes<HTMLInputElement> & { decimals?: number; negative?: boolean }) => {
  const money = decimals === 2;
  return (
    <input
      className="input"
      {...p}
      value={money && typeof value === "string" ? displayMoneyValue(value, negative) : value}
      onChange={decimals === undefined || !onChange ? onChange : (e) => {
        const v = money ? maskMoneyTyping(e.target.value, negative) : limitDecimals(e.target.value, decimals);
        if (v !== e.target.value) e.target.value = v;
        onChange(e);
      }}
    />
  );
};
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => <select className="input" {...p} />;

export function Btn({ variant = "primary", block, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "danger"; block?: boolean }) {
  return <button type="button" {...p} className={`btn ${variant}${block ? " block" : ""}`} />;
}

export const Notice = ({ kind, children }: { kind?: "error" | "ok"; children: ReactNode }) => (
  <div className={`notice${kind ? " " + kind : ""}`} role={kind === "error" ? "alert" : "status"}>{children}</div>
);

/** Marcador no fim de uma lista: quando entra na tela (a pessoa rolou até perto do fim), chama `onReach` para carregar mais linhas. */
export function ScrollSentinel({ onReach, disabled, watch }: { onReach: () => void; disabled?: boolean; watch?: unknown }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onReach); cb.current = onReach;
  useEffect(() => {
    const el = ref.current;
    if (!el || disabled || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) cb.current(); }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [disabled, watch]);   // `watch` (ex.: nº de linhas) religa o observador: se o marcador ainda estiver à vista após carregar, carrega mais
  return <div ref={ref} aria-hidden="true" style={{ height: 1 }} />;
}

export const PageHead = ({ title, sub }: { title: string; sub?: string }) => (
  <div className="page-head">
    <h1 title={sub}>{title}</h1>
  </div>
);
